<template>
  <div
    class="carousel-container"
    :style="wh"
    @mouseenter="stopCarousel"
    @mouseleave="startCarousel"
  >
    <img
      :src="currentImage!.src"
      :alt="currentImage!.alt"
      class="carousel-image"
    />
    <!-- 左侧切换按钮 -->
    <button
      class="carousel-arrow carousel-arrow-left"
      @click="previousImage"
      aria-label="上一张"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M15 18L9 12L15 6"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
    <!-- 右侧切换按钮 -->
    <button
      class="carousel-arrow carousel-arrow-right"
      @click="nextImage"
      aria-label="下一张"
    >
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M9 18L15 12L9 6"
          stroke="white"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
    <div class="carousel-indicators">
      <button
        v-for="(_, index) in images"
        :key="index"
        :class="['indicator', { active: index === currentImageIndex }]"
        @click="goToImage(index)"
        :aria-label="`跳转到第 ${index + 1} 张图片`"
      ></button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from "vue";

export interface ImageType {
  src: string;
  alt: string;
}

interface Props {
  images?: ImageType[];
  interval?: number;
  wh?: {
    width: string;
    height: string;
  };
}

const props = withDefaults(defineProps<Props>(), {
  images: () => [
    { src: "/Environment/全院/0.png", alt: "医院环境 1" },
    { src: "/Environment/全院/1.png", alt: "医院环境 2" },
    { src: "/Environment/全院/2.png", alt: "医院环境 3" },
    // { src: "/Environment/全院/3.JPG", alt: "医院环境 4" },
    // { src: "/Environment/全院/4.JPG", alt: "医院环境 5" },
    // { src: "/Environment/全院/5.JPG", alt: "医院环境 6" },
    // { src: "/Environment/全院/6.JPG", alt: "医院环境 7" },
    // { src: "/Environment/全院/7.JPG", alt: "医院环境 8" },
    // { src: "/Environment/全院/8.JPG", alt: "医院环境 9" },
    // { src: "/Environment/全院/9.JPG", alt: "医院环境 10" },
  ],
  interval: 5000,
  wh: () => ({ width: "100%", height: "400px" }),
});

const currentImageIndex = ref(0);
let carouselInterval: ReturnType<typeof setInterval> | null = null;

const currentImage = computed(() => props.images[currentImageIndex.value]);

const hasImages = computed(() => props.images.length > 0);

const changeImage = (direction: "next" | "prev") => {
  if (!hasImages.value) return;

  const length = props.images.length;
  currentImageIndex.value =
    direction === "next"
      ? (currentImageIndex.value + 1) % length
      : (currentImageIndex.value - 1 + length) % length;
};

const previousImage = () => changeImage("prev");
const nextImage = () => changeImage("next");

const goToImage = (index: number) => {
  if (!hasImages.value || index < 0 || index >= props.images.length) return;
  currentImageIndex.value = index;
};

const startCarousel = () => {
  stopCarousel();
  if (hasImages.value) {
    carouselInterval = setInterval(() => {
      changeImage("next");
    }, props.interval);
  }
};

const stopCarousel = () => {
  if (carouselInterval) {
    clearInterval(carouselInterval);
    carouselInterval = null;
  }
};

watch(
  () => props.images,
  (newImages) => {
    currentImageIndex.value = 0;
    if (newImages.length > 0) {
      startCarousel();
    } else {
      stopCarousel();
    }
  },
);

onMounted(() => {
  startCarousel();
});

onUnmounted(() => {
  stopCarousel();
});
</script>

<style scoped>
.carousel-container {
  position: relative;
  max-width: 600px;
  margin: 20px auto;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  background-size: cover;
  background-position: center;
}

.carousel-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.carousel-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background-color: rgba(0, 0, 0, 0.3);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition:
    opacity 0.3s ease,
    background-color 0.3s ease;
  z-index: 10;
  padding: 0;
}

.carousel-container:hover .carousel-arrow {
  opacity: 1;
}

.carousel-arrow:hover {
  background-color: rgba(0, 0, 0, 0.6);
  transform: translateY(-50%) scale(1.1);
}

.carousel-arrow-left {
  left: 10px;
}

.carousel-arrow-right {
  right: 10px;
}

.carousel-indicators {
  position: absolute;
  bottom: 10px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background-color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition:
    background-color 0.3s ease,
    transform 0.3s ease;
  padding: 0;
}

.indicator.active {
  background-color: #007bff;
  transform: scale(1.2);
}

.indicator:hover {
  background-color: rgba(255, 255, 255, 0.8);
}

/* 移动端适配 */
@media (max-width: 768px) {
  .carousel-container {
    max-width: 100%;
    margin: 15px auto;
    border-radius: 6px;
  }
  
  .carousel-arrow {
    width: 36px;
    height: 36px;
    opacity: 0.8; /* 移动端始终显示箭头 */
  }
  
  .carousel-arrow svg {
    width: 20px;
    height: 20px;
  }
  
  .carousel-arrow-left {
    left: 8px;
  }
  
  .carousel-arrow-right {
    right: 8px;
  }
  
  .indicator {
    width: 10px;
    height: 10px;
  }
  
  .carousel-indicators {
    bottom: 8px;
    gap: 6px;
  }
}

@media (max-width: 480px) {
  .carousel-container {
    margin: 10px auto;
    border-radius: 4px;
  }
  
  .carousel-arrow {
    width: 32px;
    height: 32px;
  }
  
  .carousel-arrow svg {
    width: 18px;
    height: 18px;
  }
  
  .indicator {
    width: 8px;
    height: 8px;
  }
}
</style>
